/**
 * Delivery lifecycle for an outbound message.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS, AND WHY IT IS SEPARATE FROM `MessageEntity.status`
 * ---------------------------------------------------------------------------
 * `MessageEntity.status` is `'sent' | 'error' | 'warning'`. Those three values
 * describe only what OUR OWN worker observed when it handed the message to a
 * provider. They cannot express what the provider or the recipient's mail
 * server subsequently did, so a message that was accepted by SendGrid and then
 * silently dropped for suppression was indistinguishable from one that landed
 * in the inbox.
 *
 * Rather than overload `status` (which existing consumers, the Inbox UI and the
 * public API all switch on), the lifecycle is modelled separately:
 *
 *   - `status`          -- UNCHANGED. Our send attempt. Backwards compatible.
 *   - `deliveryStatus`  -- what the PROVIDER told us happened.
 *   - `delivery`        -- timestamps, counters and provider identifiers.
 *   - delivery events   -- an append-only history collection, so every
 *                          transition is auditable rather than just the latest.
 *
 * ---------------------------------------------------------------------------
 * THE SIX QUESTIONS THE MODEL MUST ANSWER
 * ---------------------------------------------------------------------------
 *   1. Did we successfully create a send request?        -> status = 'sent'
 *   2. Did the provider accept it?                       -> ACCEPTED
 *   3. Did the provider attempt delivery?                -> PROCESSED / DEFERRED
 *   4. Was it delivered?                                 -> DELIVERED
 *   5. Did it permanently fail?                          -> BOUNCED/DROPPED/BLOCKED/FAILED
 *   6. Did the recipient interact?                       -> delivery.openCount / clickCount
 *
 * Interaction is deliberately NOT a status. A message can be delivered and
 * opened four times; those are orthogonal facts, not successive states.
 *
 * ---------------------------------------------------------------------------
 * NOT TO BE CONFUSED WITH `DeliveryLifecycleStatusEnum`
 * ---------------------------------------------------------------------------
 * `DeliveryLifecycleStatusEnum` (entities/workflow-run) describes a WORKFLOW
 * STEP: was it sent, skipped, merged into a digest, cancelled. It answers
 * "what did the workflow engine do with this step?".
 *
 * `DeliveryStatusEnum` here describes ONE MESSAGE's journey through an external
 * provider. It answers "what happened to this email after we handed it over?".
 * The two are complementary and both are needed; neither replaces the other.
 */
export var DeliveryStatusEnum;
(function (DeliveryStatusEnum) {
    /** Message row created; nothing handed to a provider yet. */
    DeliveryStatusEnum["PENDING"] = "pending";
    /** Queued internally for a worker to pick up. */
    DeliveryStatusEnum["QUEUED"] = "queued";
    /** Our worker called the provider and the call returned without error. */
    DeliveryStatusEnum["SENT"] = "sent";
    /**
     * The provider explicitly took custody (e.g. SendGrid 202, Twilio `accepted`).
     * THIS IS NOT DELIVERY. It is the most commonly misread state.
     */
    DeliveryStatusEnum["ACCEPTED"] = "accepted";
    /** The provider began processing / attempting delivery. */
    DeliveryStatusEnum["PROCESSING"] = "processing";
    /** Temporary failure; the provider intends to retry. Non-terminal. */
    DeliveryStatusEnum["DEFERRED"] = "deferred";
    /** Confirmed accepted by the recipient's mail server / handset. Terminal-success. */
    DeliveryStatusEnum["DELIVERED"] = "delivered";
    /** Permanent delivery failure reported by the receiving server. */
    DeliveryStatusEnum["BOUNCED"] = "bounced";
    /** The provider refused to send it at all (usually suppression). */
    DeliveryStatusEnum["DROPPED"] = "dropped";
    /** Refused for reputation/policy reasons by the receiving server. */
    DeliveryStatusEnum["BLOCKED"] = "blocked";
    /** Recipient marked it as spam. */
    DeliveryStatusEnum["SPAM_REPORTED"] = "spam_reported";
    /** Recipient unsubscribed. */
    DeliveryStatusEnum["UNSUBSCRIBED"] = "unsubscribed";
    /** We could not hand it to the provider at all. */
    DeliveryStatusEnum["FAILED"] = "failed";
    /** Transient failure on our side; a retry is scheduled. Non-terminal. */
    DeliveryStatusEnum["RETRYING"] = "retrying";
    /** Retries exhausted, or a permanent error. Terminal-failure. */
    DeliveryStatusEnum["PERMANENTLY_FAILED"] = "permanently_failed";
    /** Suppressed by OUR suppression list before the provider was contacted. */
    DeliveryStatusEnum["SUPPRESSED"] = "suppressed";
})(DeliveryStatusEnum || (DeliveryStatusEnum = {}));
/**
 * Monotonic rank used to reject out-of-order provider events.
 *
 * Provider webhooks arrive out of order and more than once. Without a rank a
 * late `processed` event would drag an already-`delivered` message backwards.
 * A transition is only applied when it strictly increases the rank, or when it
 * is a terminal override (see `canTransitionTo`).
 */
const DELIVERY_STATUS_RANK = {
    [DeliveryStatusEnum.PENDING]: 0,
    [DeliveryStatusEnum.QUEUED]: 10,
    [DeliveryStatusEnum.RETRYING]: 15,
    [DeliveryStatusEnum.SENT]: 20,
    [DeliveryStatusEnum.ACCEPTED]: 30,
    [DeliveryStatusEnum.PROCESSING]: 40,
    [DeliveryStatusEnum.DEFERRED]: 50,
    [DeliveryStatusEnum.DELIVERED]: 60,
    // Failure states outrank delivery: a bounce after a delivered event is real
    // (e.g. a delayed async bounce) and must win.
    [DeliveryStatusEnum.BOUNCED]: 70,
    [DeliveryStatusEnum.DROPPED]: 70,
    [DeliveryStatusEnum.BLOCKED]: 70,
    [DeliveryStatusEnum.FAILED]: 70,
    [DeliveryStatusEnum.SUPPRESSED]: 70,
    [DeliveryStatusEnum.PERMANENTLY_FAILED]: 80,
    // Compliance outranks everything: it is the strongest signal we can receive
    // and it must never be overwritten by a later engagement or delivery event.
    [DeliveryStatusEnum.SPAM_REPORTED]: 90,
    [DeliveryStatusEnum.UNSUBSCRIBED]: 90,
};
/** Statuses after which no further delivery progress is expected. */
export const TERMINAL_DELIVERY_STATUSES = new Set([
    DeliveryStatusEnum.DELIVERED,
    DeliveryStatusEnum.BOUNCED,
    DeliveryStatusEnum.DROPPED,
    DeliveryStatusEnum.BLOCKED,
    DeliveryStatusEnum.SPAM_REPORTED,
    DeliveryStatusEnum.UNSUBSCRIBED,
    DeliveryStatusEnum.PERMANENTLY_FAILED,
    DeliveryStatusEnum.SUPPRESSED,
]);
/** Statuses that represent a confirmed, permanent delivery failure. */
export const FAILED_DELIVERY_STATUSES = new Set([
    DeliveryStatusEnum.BOUNCED,
    DeliveryStatusEnum.DROPPED,
    DeliveryStatusEnum.BLOCKED,
    DeliveryStatusEnum.FAILED,
    DeliveryStatusEnum.PERMANENTLY_FAILED,
    DeliveryStatusEnum.SUPPRESSED,
]);
export function getDeliveryStatusRank(status) {
    return DELIVERY_STATUS_RANK[status] ?? -1;
}
/**
 * Decide whether `next` may replace `current`.
 *
 * Rules, in order:
 *   1. Anything can be set on a message with no status yet.
 *   2. `DEFERRED` may repeat -- a message can be deferred many times before it
 *      finally delivers or bounces -- so it is allowed to re-apply to itself.
 *   3. Otherwise the rank must strictly increase.
 *
 * Equal-rank transitions are refused so a duplicate webhook cannot rewrite
 * timestamps on an already-recorded state.
 */
export function canTransitionTo(current, next) {
    if (!current) {
        return true;
    }
    if (current === next) {
        return next === DeliveryStatusEnum.DEFERRED;
    }
    return getDeliveryStatusRank(next) > getDeliveryStatusRank(current);
}
/**
 * Wording guidance, deliberately colocated with the model so it is hard to
 * ignore at the call site.
 *
 * Open tracking works by embedding a 1x1 pixel. It is systematically wrong in
 * both directions: Apple Mail Privacy Protection pre-fetches images for every
 * user who enables it (false opens), and image-blocking clients never load the
 * pixel at all (false negatives). Click tracking is more reliable but corporate
 * link scanners generate phantom clicks.
 *
 * Therefore: report "open detected", never "read by user".
 */
export const OPEN_TRACKING_CAVEAT = 'An open event means an open was detected, not that the recipient read the message. ' +
    'Image proxying (e.g. Apple Mail Privacy Protection) can report opens that never happened, ' +
    'and image blocking can hide opens that did.';
/**
 * User-facing phrasing per state.
 *
 * The important line is ACCEPTED: it must never read as "delivered". That
 * single mislabel is what let operators believe an undelivered message had
 * reached its recipient.
 */
export const DELIVERY_STATUS_LABEL = {
    [DeliveryStatusEnum.PENDING]: 'Request accepted for processing',
    [DeliveryStatusEnum.QUEUED]: 'Queued',
    [DeliveryStatusEnum.SENT]: 'Sent to provider',
    [DeliveryStatusEnum.ACCEPTED]: 'Provider accepted the message (delivery not yet confirmed)',
    [DeliveryStatusEnum.PROCESSING]: 'Provider is attempting delivery',
    [DeliveryStatusEnum.DEFERRED]: 'Temporarily deferred, provider will retry',
    [DeliveryStatusEnum.DELIVERED]: 'Delivery confirmed',
    [DeliveryStatusEnum.BOUNCED]: 'Bounced',
    [DeliveryStatusEnum.DROPPED]: 'Not sent (suppressed by provider)',
    [DeliveryStatusEnum.BLOCKED]: 'Blocked by the receiving server',
    [DeliveryStatusEnum.SPAM_REPORTED]: 'Reported as spam',
    [DeliveryStatusEnum.UNSUBSCRIBED]: 'Recipient unsubscribed',
    [DeliveryStatusEnum.FAILED]: 'Delivery failed',
    [DeliveryStatusEnum.RETRYING]: 'Retrying',
    [DeliveryStatusEnum.PERMANENTLY_FAILED]: 'Permanently failed',
    [DeliveryStatusEnum.SUPPRESSED]: 'Not sent (address is suppressed)',
};
/** Reason an address ended up on the suppression list. */
export var SuppressionReasonEnum;
(function (SuppressionReasonEnum) {
    SuppressionReasonEnum["HARD_BOUNCE"] = "hard_bounce";
    SuppressionReasonEnum["SPAM_COMPLAINT"] = "spam_complaint";
    SuppressionReasonEnum["UNSUBSCRIBE"] = "unsubscribe";
    SuppressionReasonEnum["BLOCKED"] = "blocked";
    SuppressionReasonEnum["MANUAL"] = "manual";
})(SuppressionReasonEnum || (SuppressionReasonEnum = {}));
//# sourceMappingURL=delivery.js.map